/**
 * Applies the operations of a domain command to the local database in one transaction
 * (docs/07 §4.2). Statements name only the columns present in each operation, so the SDK
 * queues a `PATCH` with exactly the changed columns and a `PUT` with the new row.
 *
 * Value mapping to SQLite: booleans → 0/1, objects → JSON text, everything else as is.
 */
export function toSqliteValue(value) {
    if (value === null || value === undefined)
        return null;
    if (typeof value === 'boolean')
        return value ? 1 : 0;
    if (typeof value === 'number' || typeof value === 'string')
        return value;
    return JSON.stringify(value);
}
const IDENTIFIER = /^[a-z_][a-z0-9_]*$/;
function assertIdentifier(name) {
    if (!IDENTIFIER.test(name))
        throw new Error(`Invalid identifier: ${name}`);
    return name;
}
/** SQL statement for one operation (exported for tests and tooling). */
export function toStatement(operation) {
    const table = assertIdentifier(operation.table);
    switch (operation.op) {
        case 'PUT': {
            const entries = Object.entries(operation.data ?? {});
            const columns = ['id', ...entries.map(([column]) => assertIdentifier(column))];
            const params = [operation.id, ...entries.map(([, value]) => toSqliteValue(value))];
            return {
                sql: `INSERT OR REPLACE INTO ${table} (${columns.join(', ')}) VALUES (${columns.map(() => '?').join(', ')})`,
                params,
            };
        }
        case 'PATCH': {
            const entries = Object.entries(operation.data ?? {});
            if (entries.length === 0)
                throw new Error(`PATCH of ${table} without columns`);
            return {
                sql: `UPDATE ${table} SET ${entries.map(([column]) => `${assertIdentifier(column)} = ?`).join(', ')} WHERE id = ?`,
                params: [...entries.map(([, value]) => toSqliteValue(value)), operation.id],
            };
        }
        case 'DELETE':
            return { sql: `DELETE FROM ${table} WHERE id = ?`, params: [operation.id] };
    }
}
/** Writes the operations in a single local transaction. */
export async function applyLocalOperations(db, operations) {
    if (operations.length === 0)
        return;
    const statements = operations.map(toStatement);
    await db.writeTransaction(async (tx) => {
        for (const statement of statements) {
            await tx.execute(statement.sql, statement.params);
        }
    });
}
//# sourceMappingURL=local-executor.js.map